/**
 * Reads and writes the layout settings the SmartAdmin runtime persisted, using
 * the same localStorage key and the same JSON shape as
 * `scripts/core/saveloadscript.js`. Keeping the format identical means a user
 * who has already customised the Razor app keeps their layout when they land on
 * the SPA, and it lets the two run side by side during the migration.
 *
 * The inline script in index.html duplicates the read path so the saved layout
 * is applied before first paint — change both together.
 */

/** localStorage key used by the original saveloadscript.js. */
export const LAYOUT_SETTINGS_KEY = 'layoutSettings';

/** Colour themes shipped with the design, in the order the settings panel lists them. */
export const THEME_STYLES = [
  'nebula',
  'olive',
  'solar',
  'lunar',
  'night',
  'aurora',
  'earth',
  'flare',
  'storm',
] as const;

export type ThemeStyle = (typeof THEME_STYLES)[number];

/** Light/dark mode, mapped onto Bootstrap 5.3's `data-bs-theme`. */
export type ColorMode = 'light' | 'dark';

export interface LayoutSettings {
  /** Space-separated `set-*` class list applied to the html element. */
  htmlRoot: string;
  /** Value of `data-bs-theme`. */
  theme: ColorMode;
  /** Href of the `#theme-style` stylesheet, or '' for the default palette. */
  themeStyle: string;
}

const DEFAULT_SETTINGS: LayoutSettings = {
  htmlRoot: '',
  theme: 'light',
  themeStyle: '',
};

/**
 * The theme stylesheets are prebuilt CSS served by the SharedUI RCL. Their SCSS
 * sources are not usable — `wwwroot/scss/themes/` contains the nine entry files
 * but none of the partials they import, so they have never been compilable from
 * this repository. The compiled output under `/myui/css/themes/` is the only
 * form of these themes that exists, which is why they are linked at runtime
 * instead of being bundled.
 */
export function themeStyleUrl(style: ThemeStyle): string {
  return `/myui/css/themes/${style}.css`;
}

/** Extracts the theme name from a `#theme-style` href, or null for the default. */
export function themeStyleFromUrl(href: string | null | undefined): ThemeStyle | null {
  if (!href) return null;
  const name = href.split('/').pop()?.replace('.min.css', '').replace('.css', '').trim();
  return THEME_STYLES.includes(name as ThemeStyle) ? (name as ThemeStyle) : null;
}

/**
 * Keeps only the `set-*` classes from a class list. The original
 * `filterClass(htmlRoot.className, /^(set)-/i)` did this so that transient
 * classes — `app-mobile-menu-open`, `hide-page-scrollbar` — were never
 * persisted and could not wedge the layout across a reload.
 */
export function filterLayoutClasses(className: string): string {
  return className
    .split(/[^\w-]+/)
    .filter((cls) => /^set-/i.test(cls))
    .join(' ');
}

export function loadSettings(): LayoutSettings {
  try {
    const raw = localStorage.getItem(LAYOUT_SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };

    const parsed = JSON.parse(raw) as Partial<LayoutSettings>;
    return {
      htmlRoot: typeof parsed.htmlRoot === 'string' ? parsed.htmlRoot : '',
      theme: parsed.theme === 'dark' ? 'dark' : 'light',
      themeStyle: typeof parsed.themeStyle === 'string' ? parsed.themeStyle : '',
    };
  } catch {
    // A corrupt or unparsable value must not stop the app from rendering.
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: LayoutSettings): void {
  try {
    localStorage.setItem(LAYOUT_SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Private browsing modes can refuse writes; the layout still works, it
    // just will not survive a reload.
  }
}

export function clearSettings(): void {
  try {
    localStorage.removeItem(LAYOUT_SETTINGS_KEY);
  } catch {
    /* nothing useful to do */
  }
}
